// utils/api/esim.js
import API from './index.js';

const normAxios = (err, fallback = "Request failed") => {
  // ✅ ignore abort/cancel
  if (err?.name === "CanceledError") return;
  if (err?.code === "ERR_CANCELED") return;
  if (String(err?.message || "").toLowerCase().includes("canceled")) return;

  const msg =
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    err?.message ||
    fallback;

  const e = new Error(msg);
  e.status = err?.response?.status;
  e.data = err?.response?.data;
  throw e;
};


const makeQS = (obj = {}) =>
  new URLSearchParams(
    Object.entries(obj).flatMap(([k, v]) => {
      if (v === undefined || v === null || v === "") return [];
      if (Array.isArray(v)) return [[k, v.join(",")]];
      return [[k, v]];
    })
  ).toString();

/**
 * Fetch a page of eSIM offers (catalog).
 * Set `all: true` to request everything in one call (backend should support `_all=1` or `_limit=0`).
 */
export async function fetchEsimOffers({
  all = false,
  limit = 20,
  offset = 0,
  country,
  regions,
  subType,
  brand,
  q,
  priceMin,
  priceMax,
  signal,
} = {}) {
  const safeLimit = all ? 0 : Math.max(0, Math.min(200, Number(limit) || 20));
  const params = {
    _limit: safeLimit,
    _all: all ? 1 : undefined,
    _offset: all ? 0 : Math.max(0, Number(offset) || 0),
    country: country || undefined,
    regions: regions || undefined,
    subType: subType || undefined,
    brand: brand || undefined,
    q: q || undefined,
    priceMin: priceMin ?? undefined,
    priceMax: priceMax ?? undefined,
  };

  let res;
  try {
    res = await API.get("/catalog/esim/offers", { params, signal });
  } catch (err) {
    // ✅ Ignore AbortController / Axios cancellation
    const msg = String(err?.message || "").toLowerCase();
    if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || msg.includes("canceled")) {
      return {
        list: [],
        total: 0,
        limit: safeLimit,
        offset: params._offset ?? 0,
        debug: { canceled: true },
      };
    }
    throw err;
  }

  const data = res?.data;

  const list = Array.isArray(data?.list)
    ? data.list
    : Array.isArray(data)
    ? data
    : [];

  const total = Number.isFinite(data?.total) ? data.total : list.length;

  return {
    list,
    total,
    limit: data?.limit ?? safeLimit,
    offset: data?.offset ?? (all ? 0 : Math.max(0, Number(offset) || 0)),
    debug: data?.debug,
  };
}


/** Fetch all ESIM offers across pages (careful: large). */
export async function fetchAllEsimOffers({
  pageSize = 200,
  country,
  regions,
  subType,
  priceMin,
  priceMax,
  signal,
} = {}) {
  const size = Math.max(1, Math.min(200, Number(pageSize) || 200));

  // Try single-call full fetch first
  try {
    const { list, total, limit } = await fetchEsimOffers({
      all: true,
      country,
      regions,
      subType,
      priceMin,
      priceMax,
      signal,
    });
    if (Array.isArray(list) && (limit === 0 || list.length === total || total <= list.length)) {
      return list;
    }
  } catch {
    // fall back to pagination
  }

  // paginate
  const out = [];
  let off = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { list: page } = await fetchEsimOffers({
      limit: size,
      offset: off,
      country,
      regions,
      subType,
      priceMin,
      priceMax,
      signal,
    });
    const batch = Array.isArray(page) ? page : [];
    out.push(...batch);
    if (batch.length < size) break;
    off += size;
  }
  return out;
}

export async function fetchEsimOfferById(offerId) {
  try {
    // point at catalog route
    const { data } = await API.get(`/catalog/esim/offers/${encodeURIComponent(offerId)}`);
    // handle both envelope {offer} or flat doc
    return data.offer ?? data;
  } catch (err) {
    normAxios(err, "Failed to load eSIM offer");
  }
}

// Purchases (JWT-backed)
export async function purchaseEsim({ offerId, iccid } = {}) {
  try {
    if (!offerId) throw new Error("offerId is required");
    const { data } = await API.post("/esim/purchases", { offerId, iccid });
    return data;
  } catch (err) {
    normAxios(err, "Failed to create eSIM purchase");
  }
}

export async function fetchEsimPurchases({ limit = 20, offset = 0, status = "", offerId = "" } = {}) {
  try {
    const qs = makeQS({ _limit: limit, _offset: offset, status, offerId });
    const { data } = await API.get(`/esim/purchases?${qs}`);
    return data;
  } catch (err) {
    normAxios(err, "Failed to load eSIM purchases");
  }
}

export async function fetchEsimTransaction(transactionId) {
  try {
    const { data } = await API.get(`/esim/purchases/${encodeURIComponent(transactionId)}`);
    return data.order;
  } catch (err) {
    normAxios(err, "Failed to load eSIM transaction");
  }
}

export async function fetchEsimQRCode(transactionId) {
  try {
    const { data } = await API.get(`/esim/purchases/${encodeURIComponent(transactionId)}/qrcode`);
    return data;
  } catch (err) {
    normAxios(err, "Failed to load eSIM QR");
  }
}

export async function refundEsimPurchase(transactionId) {
  try {
    const { data } = await API.post(`/esim/purchases/${encodeURIComponent(transactionId)}/refund`);
    return data;
  } catch (err) {
    normAxios(err, "Failed to refund eSIM purchase");
  }
}

export async function fetchEsimPlans(iccId) {
  try {
    const { data } = await API.get(`/esim/${encodeURIComponent(iccId)}/plans`);
    return { total: data.total ?? 0, list: data.list ?? [] };
  } catch (err) {
    normAxios(err, "Failed to load eSIM usage");
  }
}

export async function fetchEsimUsageByTransaction(transactionId) {
  const order = await fetchEsimTransaction(transactionId);
  const iccid = order?.confirmation?.iccid;
  if (!iccid) throw new Error("No ICCID available for this transaction yet.");
  return fetchEsimPlans(iccid);
}
